// The saved-views data layer (spec 16 §5, the five routes under
// `/v1/saved-views`). The house posture: the generated api-client only
// (doctrine 3), `{signal}` threaded through every read so a refresh cancels on
// the socket (lib/query.ts), and an Idempotency-Key on every mutation, since
// the three writes are not idempotency-exempt. The key is the CALLER's: the
// form that makes a write holds one key from its first send until the server
// has answered (`../lib/write-key.ts`, V2), so this layer takes it with each
// write and mints none.
//
// ONE READ PER SCREEN. `GET /v1/saved-views?screen_key=` answers the screen's
// system views, the caller's own and the tenant's shared ones in one list,
// bounded by the caps (20 own and 20 shared), so it has no cursor. Every
// write updates that list in the cache FIRST and then refreshes it. The order
// matters to the switcher: a Save as new writes `view=<new id>` into the
// address, and a list that did not yet hold the new id would read that
// address as a view that no longer exists (§3.12 item 9) and strip it again.
// A delete takes the row out of the cache for the mirror reason.
//
// A WRITE NEEDS THE NETWORK (W3, PR-E4 wave 5). React Query's default
// `networkMode: "online"` pauses a mutation while the browser is offline and
// sends it when the network returns, so Save as new sat on a busy "Saving…"
// with every exit of its modal inert, and the write would land later on a
// list the reader may have changed since. The three writes run in `always`
// mode and refuse before any request when the browser is offline, with
// `ViewsOfflineError`, which the form shows as the offline sentence. Nothing
// is queued, and an offline refusal refreshes nothing, since nothing reached
// the server.
//
// A LOST ANSWER (V2, PR-E4 wave 6). A request that left the browser and got
// no answer back, because the connection dropped or the browser went offline
// on the way, may have been applied. It fails with `ViewsResponseLostError`,
// and the list is read again, so the row shows what the server now holds
// (React Query holds that read paused until the network returns). The form
// keeps its key, and its retry replays the server's first answer if the first
// try landed. A problem the server sent is its answer, whatever the network
// did afterwards, and passes through unchanged. A 5xx with no problem body is
// a gateway's (a load balancer that gave up on the API), not the server's, so
// it is a lost answer too: the API may have applied the write behind it.

import {
  onlineManager,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery } from "../../../../lib/query";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, unwrap, unwrapVoid } from "../../../../lib/request";
import { answeredByGateway, ViewsOfflineError, ViewsResponseLostError } from "../lib/view-errors";
import type { SavedView, SavedViewScreenKey } from "../lib/view-state";

type S = components["schemas"];

export type SavedViewListResponse = S["SavedViewListResponse"];
export type SavedViewCreateRequest = S["SavedViewCreateRequest"];
export type SavedViewPatchRequest = S["SavedViewPatchRequest"];

export const savedViewKeys = {
  all: () => ["saved-views"] as const,
  list: (screenKey: SavedViewScreenKey) => ["saved-views", screenKey] as const,
};

/** `GET /v1/saved-views?screen_key=`. */
export async function fetchSavedViews(
  screenKey: SavedViewScreenKey,
  signal?: AbortSignal,
): Promise<SavedViewListResponse> {
  const res = await client.GET("/v1/saved-views", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { screen_key: screenKey } },
  });
  return unwrap(res, "Could not load the saved views");
}

/** The screen's views. The app-wide read policy applies: a 4xx is answered
 * once and never retried (lib/query.ts `shouldRetryRead`). */
export function useSavedViewList(screenKey: SavedViewScreenKey) {
  return useQuery({
    queryKey: savedViewKeys.list(screenKey),
    queryFn: async ({ signal }) => fetchSavedViews(screenKey, signal),
  });
}

function upsertCached(qc: QueryClient, screenKey: SavedViewScreenKey, view: SavedView): void {
  qc.setQueryData<SavedViewListResponse>(savedViewKeys.list(screenKey), (old) => {
    const items = old?.items ?? [];
    const present = items.some((item) => item.id === view.id);
    return {
      items: present ? items.map((item) => (item.id === view.id ? view : item)) : [...items, view],
    };
  });
}

function removeCached(qc: QueryClient, screenKey: SavedViewScreenKey, id: string): void {
  qc.setQueryData<SavedViewListResponse>(savedViewKeys.list(screenKey), (old) =>
    old === undefined ? old : { items: old.items.filter((item) => item.id !== id) },
  );
}

/** Run one write against the network, or refuse it at once when there is
 * none (W3). A request that got no answer back fails as a lost answer (V2),
 * and so does a gateway's bare 5xx, which is not the server's answer. */
async function online<T>(send: () => Promise<T>): Promise<T> {
  if (!onlineManager.isOnline()) throw new ViewsOfflineError();
  try {
    return await send();
  } catch (caught) {
    if (answeredByGateway(caught)) throw new ViewsResponseLostError({ cause: caught });
    if (caught instanceof RequestError || caught instanceof UnauthorizedError) throw caught;
    throw new ViewsResponseLostError({ cause: caught });
  }
}

/** The refresh after a write, skipped for an offline refusal, which never
 * reached the server. A lost answer refreshes, since the write may have
 * landed.
 *
 * The refresh is started and NOT returned. React Query awaits a promise that
 * `onSettled` returns before it settles the mutation (query-core 5.101,
 * `Mutation.execute`), so a returned refresh made every write's promise wait
 * for the list's re-read. Under the app's read policy (main.tsx, `retry:
 * shouldRetryRead`) a re-read that fails as a dropped socket is retried after
 * 1, 2 and 4 s and held while the browser is offline, and a form whose answer
 * was lost stayed busy with every exit inert for that long, or for good once
 * the browser went offline (§9.29). The cache already holds the write's
 * result before the address is written (`upsertCached`, `removeCached`), so
 * nothing waits on the re-read. */
function refreshAfter(qc: QueryClient, screenKey: SavedViewScreenKey, error: unknown): void {
  if (error instanceof ViewsOfflineError) return;
  void refreshQuery(qc, savedViewKeys.list(screenKey));
}

export interface SavedViewCreateInput {
  readonly body: SavedViewCreateRequest;
  /** The key the form holds for this write (V2). */
  readonly idempotencyKey: string;
}

/** `POST /v1/saved-views` (Save as new). The 201 is the new view. */
export function useCreateSavedView(screenKey: SavedViewScreenKey) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async ({ body, idempotencyKey }: SavedViewCreateInput) =>
      online(async () => {
        const res = await client.POST("/v1/saved-views", {
          params: { header: { "Idempotency-Key": idempotencyKey } },
          body,
        });
        return unwrap(res, "Could not save the view");
      }),
    onSuccess: (view) => {
      upsertCached(qc, screenKey, view);
    },
    onSettled: (_data, error) => {
      refreshAfter(qc, screenKey, error);
    },
  });
}

export interface SavedViewPatchInput {
  readonly id: string;
  readonly body: SavedViewPatchRequest;
  /** The key the form holds for this write (V2). */
  readonly idempotencyKey: string;
}

/** `PATCH /v1/saved-views/{id}` (Save, rename, share, unshare). */
export function useUpdateSavedView(screenKey: SavedViewScreenKey) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async (input: SavedViewPatchInput) =>
      online(async () => {
        const res = await client.PATCH("/v1/saved-views/{id}", {
          params: {
            path: { id: input.id },
            header: { "Idempotency-Key": input.idempotencyKey },
          },
          body: input.body,
        });
        return unwrap(res, "Could not update the view");
      }),
    onSuccess: (view) => {
      upsertCached(qc, screenKey, view);
    },
    onSettled: (_data, error) => {
      refreshAfter(qc, screenKey, error);
    },
  });
}

export interface SavedViewDeleteInput {
  readonly id: string;
  /** The key the form holds for this write (V2). */
  readonly idempotencyKey: string;
}

/** `DELETE /v1/saved-views/{id}` (the author's delete, or a moderator's). A
 * hard delete, so the row leaves the cache before the refresh answers. */
export function useDeleteSavedView(screenKey: SavedViewScreenKey) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async ({ id, idempotencyKey }: SavedViewDeleteInput) =>
      online(async () => {
        const res = await client.DELETE("/v1/saved-views/{id}", {
          params: { path: { id }, header: { "Idempotency-Key": idempotencyKey } },
        });
        unwrapVoid(res, "Could not delete the view");
        return id;
      }),
    onSuccess: (id) => {
      removeCached(qc, screenKey, id);
    },
    onSettled: (_data, error) => {
      refreshAfter(qc, screenKey, error);
    },
  });
}
